import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import {
  useAuthStore,
  useChangePassword,
  useSelfDeleteConfirm,
  useSelfDeletePreview,
  useUpdateProfile,
} from '@wordpuppi/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';

export const Route = createFileRoute('/user/')({
  component: ProfilePage,
});

function ProfilePage() {
  const { isAuthenticated, user, logout, checkAuth, setToken } = useAuthStore();
  const navigate = useNavigate();

  // AB#540: the delete-confirm gate compares against user.email, so a missing
  // email would leave it permanently disabled. The password-login path now
  // hydrates the user in the store, but the social-login path (setToken) does
  // not — refresh from /auth/current if email is absent so the gate can enable.
  useEffect(() => {
    if (isAuthenticated && !user?.email) {
      void checkAuth();
    }
  }, [isAuthenticated, user?.email, checkAuth]);

  // ---- Display name ------------------------------------------------------
  const updateProfile = useUpdateProfile();
  const [name, setName] = useState(user?.name ?? '');

  const handleSaveName = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (trimmed.length < 2) {
      toast.error('Name must be at least 2 characters.');
      return;
    }
    try {
      await updateProfile.mutateAsync({ name: trimmed });
      // Refresh the cached user so the account menu label updates immediately.
      await checkAuth();
      toast.success('Name updated.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not update name');
    }
  };

  // ---- Password ----------------------------------------------------------
  const changePassword = useChangePassword();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwError, setPwError] = useState<string | null>(null);

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwError(null);
    if (newPassword.length < 8) {
      setPwError('New password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPwError('New passwords do not match.');
      return;
    }
    try {
      const res = await changePassword.mutateAsync({
        current_password: currentPassword,
        new_password: newPassword,
      });
      // Password-change session revocation: the change killed every prior token,
      // including this one. Swap in the fresh token the server returned so the
      // current session survives (other sessions/devices are signed out).
      if (res.data?.token) setToken(res.data.token);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      toast.success('Password changed.');
    } catch (err) {
      // Loud, distinguishable wrong-current-password (server code
      // current_password_invalid). Never assume success.
      setPwError(err instanceof Error ? err.message : 'Could not change password');
    }
  };

  // ---- Danger zone: delete account --------------------------------------
  const previewDelete = useSelfDeletePreview();
  const confirmDelete = useSelfDeleteConfirm();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [cancellations, setCancellations] = useState<string[]>([]);
  const [previewMessage, setPreviewMessage] = useState<string | null>(null);
  const [deleteBlocked, setDeleteBlocked] = useState<string | null>(null);
  const [confirmEmail, setConfirmEmail] = useState('');

  const openDelete = async () => {
    setConfirmEmail('');
    setCancellations([]);
    setPreviewMessage(null);
    setDeleteBlocked(null);
    setDeleteOpen(true);
    try {
      const res = await previewDelete.mutateAsync();
      setCancellations(res.data?.cancellations ?? []);
      setPreviewMessage(res.data?.message ?? null);
    } catch (err) {
      // e.g. 409 sole_owner_block — deletion can't proceed; surface why.
      setDeleteBlocked(
        err instanceof Error ? err.message : 'This account cannot be deleted right now.',
      );
    }
  };

  const emailMatches =
    !!user?.email && confirmEmail.trim().toLowerCase() === user.email.toLowerCase();

  const handleConfirmDelete = async () => {
    if (!emailMatches || deleteBlocked) return;
    try {
      await confirmDelete.mutateAsync();
      setDeleteOpen(false);
      toast.success('Your account has been deleted.');
      await logout();
      navigate({ to: '/login' });
    } catch (err) {
      setDeleteBlocked(err instanceof Error ? err.message : 'Could not delete account');
    }
  };

  return (
    <>
      <div>
        <h1 className="text-2xl font-semibold">Profile</h1>
        <p className="text-sm text-muted-foreground">
          Manage your name, password, and account.
        </p>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="space-y-2">
            <Label htmlFor="account-email">Email</Label>
            <output id="account-email" className="block break-all text-sm">
              {user?.email || '—'}
            </output>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Display name</CardTitle>
          <CardDescription>The name shown across WordPuppi and to your team.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSaveName} className="space-y-4">
            <div className="space-y-2 max-w-sm">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="name"
                required
                minLength={2}
              />
            </div>
            <Button
              type="submit"
              disabled={updateProfile.isPending || name.trim() === (user?.name ?? '')}
            >
              {updateProfile.isPending ? 'Saving...' : 'Save name'}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Password</CardTitle>
          <CardDescription>
            Enter your current password to set a new one.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangePassword} className="space-y-4">
            {pwError && (
              <div className="p-3 bg-destructive/10 text-destructive rounded-lg text-sm">
                {pwError}
              </div>
            )}
            <div className="space-y-2 max-w-sm">
              <Label htmlFor="current-password">Current password</Label>
              <Input
                id="current-password"
                type="password"
                value={currentPassword}
                onChange={(e) => {
                  setCurrentPassword(e.target.value);
                  setPwError(null);
                }}
                autoComplete="current-password"
                required
              />
            </div>
            <div className="space-y-2 max-w-sm">
              <Label htmlFor="new-password">New password</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  setPwError(null);
                }}
                autoComplete="new-password"
                required
                minLength={8}
              />
              <p className="text-xs text-muted-foreground">At least 8 characters.</p>
            </div>
            <div className="space-y-2 max-w-sm">
              <Label htmlFor="confirm-password">Confirm new password</Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  setPwError(null);
                }}
                autoComplete="new-password"
                required
                minLength={8}
              />
            </div>
            <Button
              type="submit"
              disabled={
                changePassword.isPending ||
                !currentPassword ||
                !newPassword ||
                !confirmPassword
              }
            >
              {changePassword.isPending ? 'Changing...' : 'Change password'}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card className="border-destructive/40">
        <CardHeader>
          <CardTitle className="text-destructive">Danger zone</CardTitle>
          <CardDescription>
            Permanently delete your account. This cancels any active billing and
            removes your solo sites and data.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button variant="destructive" onClick={openDelete}>
            Delete account
          </Button>
        </CardContent>
      </Card>

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete your account?</DialogTitle>
            <DialogDescription>
              {deleteBlocked
                ? deleteBlocked
                : previewMessage ??
                  'Deleting your account will remove your sites and data. This cannot be undone.'}
            </DialogDescription>
          </DialogHeader>

          {!deleteBlocked && cancellations.length > 0 && (
            <div className="rounded-lg border border-border p-3 text-sm">
              <p className="font-medium">This will cancel:</p>
              <ul className="mt-1 list-disc pl-5 text-muted-foreground">
                {cancellations.map((c) => (
                  <li key={c}>{c}</li>
                ))}
              </ul>
            </div>
          )}

          {!deleteBlocked && (
            <div className="space-y-2">
              <Label htmlFor="confirm-email">
                Type your email <span className="font-mono">{user?.email}</span> to confirm
              </Label>
              <Input
                id="confirm-email"
                type="email"
                value={confirmEmail}
                onChange={(e) => setConfirmEmail(e.target.value)}
                autoComplete="off"
                placeholder={user?.email}
              />
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>
              Cancel
            </Button>
            {!deleteBlocked && (
              <Button
                variant="destructive"
                disabled={!emailMatches || confirmDelete.isPending}
                onClick={handleConfirmDelete}
              >
                {confirmDelete.isPending ? 'Deleting...' : 'Delete account'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
